export const PRODUCT_PAGE_SIZE = 60
const SCALE_WEIGHT_ROUND_UP_DIGIT = 2
const RETAIL_SALE_TIME_ZONE = 'Asia/Phnom_Penh'

const validExpiryDate = value => {
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(4, 6))
  const day = Number(value.slice(6, 8))
  const date = new Date(Date.UTC(year, month - 1, day))

  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
}

/**
 * Parse a retail scan: 1–13 digits for a normal barcode, or 27 digits as
 * [13-digit product barcode][6-digit weight][8-digit date in YYYYMMDD].
 * The legacy 19-digit scale format omits the date. Weight is kilograms to
 * three decimals; its third decimal rounds the two-decimal quantity up when
 * that digit is at least SCALE_WEIGHT_ROUND_UP_DIGIT. Invalid calendar dates
 * are rejected; the 27-digit result includes the expiry date for scan checks.
 *
 * @param {string|number} raw Scanned barcode value.
 * @returns {{barcode: string, kilograms: number|null, raw: string, expiryDate?: string}|null} Parsed scan.
 */
export const parseRetailScan = raw => {
  const code = String(raw || '').trim()

  if (/^\d{1,13}$/.test(code)) {
    return { barcode: code, kilograms: null, raw: code }
  }

  if (/^\d{19}(?:\d{8})?$/.test(code)) {
    const expiryDate = code.length === 27 ? code.slice(19) : null
    if (expiryDate && !validExpiryDate(expiryDate)) return null

    const thousandths = Number(code.slice(13, 19))
    const hundredths = Math.floor(thousandths / 10) + (thousandths % 10 >= SCALE_WEIGHT_ROUND_UP_DIGIT ? 1 : 0)
    const kilograms = hundredths / 100
    if (kilograms > 0) {
      return {
        barcode: code.slice(0, 13),
        kilograms,
        raw: code,
        ...(expiryDate ? { expiryDate } : {})
      }
    }
  }

  return null
}

/**
 * Check whether a scan's expiry day has passed in the retail sale timezone.
 *
 * @param {string} expiryDate Valid YYYYMMDD date from a scale barcode.
 * @param {Date} now Current time; injectable for deterministic checks.
 * @returns {boolean} Whether the barcode expired before today.
 */
export const isRetailScanExpired = (expiryDate, now = new Date()) => {
  if (!expiryDate) return false

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: RETAIL_SALE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now)
  const date = parts.reduce((values, part) => {
    if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
      values[part.type] = part.value
    }
    return values
  }, {})
  const today = `${date.year}${date.month}${date.day}`

  return expiryDate < today
}

/**
 * Build the query parameters used to fetch a page of retail-sale products.
 *
 * @param {Object} order Current order filters.
 * @param {number} page Product page number.
 * @returns {Object} Product request query parameters.
 */
export const buildProductQuery = (order, page) => {
  const params = {
    page,
    per_page: PRODUCT_PAGE_SIZE
  }
  const search = order.search.trim()

  if (order.activeCategoryId !== null) {
    params.category_id = order.activeCategoryId
  }

  if (search) {
    params.search = search
  }

  return params
}

/**
 * Convert the product API response shape into the UI product shape.
 *
 * @param {Array} products Raw products returned by the API.
 * @returns {Array} Products ready for retail-sale components.
 */
export const mapProducts = products => {
  if (!Array.isArray(products)) return []

  return products.map(product => ({
    id: product.product_id,
    name: product.product_name_en,
    nameKh: product.product_name_kh,
    barcode: product.barcode,
    category_id: product.category_id,
    category: product.category_name_en,
    categoryKh: product.category_name_kh,
    pro_sale_code_id: product.pro_sale_code_id,
    uom_id: product.uom_id,
    promotion: product.promotion || null,
    stock: Number(product.stock || 0),
    price: Number(product.price || 0),
    unit: product.uom_name_en,
    unitKh: product.uom_name_kh,
    image: product.photo
  }))
}

/**
 * Resolve the current page and whether another product page is available.
 *
 * @param {Object|null} meta Pagination metadata returned by the API.
 * @param {number} page Requested page number.
 * @param {number} productCount Number of products returned.
 * @returns {{currentPage: number, hasMore: boolean}} Pagination state.
 */
export const getProductPagination = (meta, page, productCount) => {
  if (!meta) {
    return {
      currentPage: page,
      hasMore: productCount === PRODUCT_PAGE_SIZE
    }
  }

  const currentPage = Number(meta.current_page || page)

  return {
    currentPage,
    hasMore: currentPage < Number(meta.last_page || page)
  }
}
